import { useCallback, useState } from 'react'
import { StaleStateError } from '../api/client'
import type { StaleSnapshot } from '../api/core'

export interface Conflict {
  message: string
  serverState: StaleSnapshot | null
}

/** The banner payload for a 412 `StaleStateError`; null for any other error. */
export function toConflict(e: unknown): Conflict | null {
  return e instanceof StaleStateError ? { message: e.serverMessage, serverState: e.currentState } : null
}

/**
 * Shared optimistic-concurrency + busy-action plumbing every mutating page
 * repeated by hand: a `conflict` (for the StaleConflictBanner), an `error`
 * string, and a `run()` wrapper that toggles a busy flag, clears prior errors,
 * routes a 412 `StaleStateError` to the conflict banner, and surfaces anything
 * else as an error.
 */
export function useStaleConflict() {
  const [conflict, setConflict] = useState<Conflict | null>(null)
  const [error, setError] = useState<string | null>(null)

  /** Raise the conflict banner iff `e` is a 412 StaleStateError; return whether
   *  it was one. Lets callers decide how to surface non-conflict errors (e.g. a
   *  child that shows its own local error: `if (!handle(e)) setLocalError(e)`). */
  const handle = useCallback((e: unknown): boolean => {
    const c = toConflict(e)
    if (c) setConflict(c)
    return c !== null
  }, [])

  /** Route one caught error: a 412 raises the conflict banner, anything else
   *  becomes this hook's error string. For try/catch sites outside `run`. */
  const report = useCallback(
    (e: unknown) => {
      if (!handle(e)) setError(String(e))
    },
    [handle],
  )

  const run = useCallback(
    async (setBusy: (b: boolean) => void, action: () => Promise<void>) => {
      setBusy(true)
      setError(null)
      try {
        await action()
      } catch (e) {
        report(e)
      } finally {
        setBusy(false)
      }
    },
    [report],
  )

  /** Clear the conflict + error (call before re-loading after a rebase). */
  const clear = useCallback(() => {
    setConflict(null)
    setError(null)
  }, [])

  return { conflict, error, setError, run, report, handle, clear }
}
